import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import OpenAI from 'openai';
import * as XLSX from 'xlsx';

// Initialize OpenAI first
const getOpenAIKey = () => {
  const key = process.env.REACT_APP_OPENAI_API_KEY;
  if (!key) {
    console.error('OpenAI API key not found in environment variables');
    throw new Error('OpenAI API key not configured');
  }
  return key;
};

const openai = new OpenAI({
  apiKey: getOpenAIKey(),
  dangerouslyAllowBrowser: true
});

// Add debug logging
console.log('Environment check:', {
  nodeEnv: process.env.NODE_ENV,
  hasOpenAIKey: !!process.env.REACT_APP_OPENAI_API_KEY,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID
});

// Cost estimates per 1K tokens (as of March 2024)
const GPT4_COST = {
  input: 0.01,   // $0.01 per 1K input tokens
  output: 0.03   // $0.03 per 1K output tokens
};

// Rate limiting settings
const RATE_LIMIT = {
  requestsPerMinute: 200,  // OpenAI's default rate limit
  tokensPerMinute: 40000, // GPT-4 token limit per minute
  pauseMs: 300,           // Pause between requests
};

// Estimate tokens in a string (rough approximation)
const estimateTokens = (text) => Math.ceil(text.length / 4);

let isCancelled = false;

export const cancelEnrichment = () => {
  isCancelled = true;
};

// Make cancelEnrichment available globally for emergency stops
window.cancelEnrichment = cancelEnrichment;

async function calculateCostEstimate(verses) {
  let totalInputTokens = 0;
  let estimatedOutputTokens = 0;
  
  verses.forEach(verse => {
    // Estimate input tokens from verse text and reference
    totalInputTokens += estimateTokens(verse.text + verse.reference + 
      "Analyze this verse and provide contextual meaning, theme scores, etc.");
    
    // Estimate output tokens (typical response size)
    estimatedOutputTokens += 500; // Conservative estimate per verse
  });

  const estimatedCost = (
    (totalInputTokens / 1000) * GPT4_COST.input +
    (estimatedOutputTokens / 1000) * GPT4_COST.output
  ).toFixed(2);

  return {
    inputTokens: totalInputTokens,
    outputTokens: estimatedOutputTokens,
    cost: estimatedCost
  };
}

// Rate limiter class
class RateLimiter {
  constructor() {
    this.requests = [];
    this.tokens = [];
  }

  async checkLimit() {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    
    // Clean up old requests
    this.requests = this.requests.filter(time => time > oneMinuteAgo);
    this.tokens = this.tokens.filter(({time}) => time > oneMinuteAgo);
    
    // Check limits
    if (this.requests.length >= RATE_LIMIT.requestsPerMinute) {
      const oldestRequest = this.requests[0];
      const waitTime = 60000 - (now - oldestRequest);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
    
    const totalTokens = this.tokens.reduce((sum, {tokens}) => sum + tokens, 0);
    if (totalTokens >= RATE_LIMIT.tokensPerMinute) {
      const oldestToken = this.tokens[0].time;
      const waitTime = 60000 - (now - oldestToken);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }
  }

  addRequest(tokens = 0) {
    this.requests.push(Date.now());
    this.tokens.push({ time: Date.now(), tokens });
  }
}

const rateLimiter = new RateLimiter();

// Debug function to check our process
const debugEnrichmentProcess = async (verse, selectedFields) => {
  console.log('🔍 ENRICHMENT DEBUG:');
  console.log('1. Input Check:', {
    verse: verse.reference,
    selectedFields,
    currentThemeScores: verse.themeMetadata
  });

  // Expanded prompt for all fields, preserving specific language
  const prompt = `
    Given the following Bible verse, generate a JSON object with the following fields. If a field is not directly inferable, use your best judgment or leave it as an empty string or empty array.

    Reference: ${verse.reference}
    Text: ${verse.text}

    Please provide the following fields:
    - bibleUrl: A direct Bible.com URL to the NIV version of this verse (format: https://www.bible.com/bible/111/{book}.{chapter}.{verse}.NIV)
    - reflectionQuestion: Write a single, practical reflection question for this verse that:
        - Encourages the reader to think about a specific situation or relationship in their life.
        - Is open-ended and invites personal storytelling, imagination, or self-examination.
        - Avoids simply restating the verse or asking generic "How do you apply X?" questions.
        - Uses varied question structures (e.g., "If you could...", "When was a time...", "Who in your life...", "What would it look like if...", "Why do you think...", "Describe a moment when...").
        - Is gentle and invitational, not confrontational or threatening.
        - Is appropriate for a wide range of personalities and spiritual maturity levels.
        - Avoids yes/no questions and encourages deeper thought or sharing.
        - Example: Instead of "How do you apply faith to a friendship?" ask "If there was one friendship situation where you could apply faith, which one would it be and why?"
        - Vary the style and focus of the question for each verse, so that not all questions follow the same template.
    - tags: 3-5 relevant, comma-separated keywords (themes or topics found in this verse)
    - categories: 2-3 broad categories (e.g., Spiritual Growth, Prayer, Doctrine, Relationships, Character, Mission/Evangelism)
    - growthArea: Exactly one of: Spiritual Development, Holisitic Personal Growth, Relational Development, Physical Development, Vocational Development, Missional Development, Emotional Development
    - devTopic: Exactly one of: Christian Character and Virtues, Cultural Engagement and Worldview, Dealing with Trials and Suffering, Discipleship and Mentoring Others, Evangelism and Sharing Your Faith, Finances - Handling Money Gods Way, Identity in Christ and Intrinsic Value, Leadership and Servanthood, Marriage and Family, Mental and Emotional Health, Moral and Ethical Decision-Making, Overcoming Temptation and Sin, Personal Faith and Relationship with God, Prayer and Spiritual Disciplines, Relationships and Community, Sexual Purity and Enjoyment, Spiritual Warfare, Stewardship of the Body, Time Management and Priorities, Understanding the Bible, Work and Stewardship
    - concept: Key theological or practical concept
    - difficultyLevel: Number 1-5 (1=foundational, 5=deep/complex)
    - relatedVerses: 2-4 comma-separated related verse references
    - contextualMeaning: 2-3 sentences explaining practical application, covering: the core message and principle, how it applies to modern life, and practical ways to implement this truth
    - themeMetadata: An object with numeric scores (0-1) for each of the following:
      - emotional: how much the verse is about the emotional aspects of a human experience
      - spiritual: how much the verse is about the spiritual aspects of a human experience
      - relational: family, friends, community, etc. and how much the verse is about those relationships
      - vocational: vocational and/or scholastic application
      - physical: how much the verse is about the physical aspects of a human experience
      - missional: how much the verse is about the focus on having a calling to help change the world through sharing the gospel
    - ageRelevance: An object with numeric scores (0-1) for each of the following keys, representing the developmental appropriateness and relevance of the verse to each age/life stage. 
      When scoring, consider:
        - Language complexity (is it simple enough for a child, or more suited for adults?)
        - Abstractness of ideas (concrete for children, more abstract for older groups)
        - Emotional and spiritual maturity required to understand and apply the verse
        - Life experience needed to relate to the verse
      Keys:
        - elementary: Elementary Age (simple, concrete, basic truths)
        - middleSchool: Middle School (accessible, some abstract ideas)
        - highSchool: High School (can handle more challenge/abstractness)
        - youngAdult: Young Adult (mature reasoning, life application)
        - middleAge: Middle Age (life experience, deeper reflection)
        - wisdomYears: Wisdom Years (deepest reflection, legacy, perspective)
    - relationshipRelevance: An object with numeric scores (0-1) for each of the following keys, representing the verse's direct relevance to the unique relational, emotional, and life circumstances of each status. 
      When scoring, consider:
        - Does the verse address issues of identity, belonging, or friendship? (youth, singles)
        - Does it speak to romantic love, commitment, or marriage? (dating, engaged, married)
        - Does it offer wisdom for parenting or family life? (married with kids, grandparents)
        - Does it provide comfort, hope, or encouragement for those experiencing loss or transition? (separated, divorced, widowed)
      Keys:
        - youth: Youth (identity, friendship, growth)
        - singleYoungAdult: Single Young Adult (calling, purpose, community)
        - singleAdult: Single Adult (purpose, contentment, relationships)
        - dating: Dating (love, purity, discernment)
        - engaged: Engaged (commitment, preparation)
        - earlyMarriage: Early Marriage (foundation, unity)
        - experiencedMarriage: Experienced Marriage (endurance, partnership)
        - marriedWithYoungKids: Married with young kids (parenting, patience)
        - marriedWithOlderKids: Married with older kids (guidance, legacy)
        - grandparents: Grandparents (wisdom, blessing, legacy)
        - separated: Separated (healing, hope)
        - divorced: Divorced (restoration, comfort)
        - widowed: Widowed (comfort, hope, legacy)
    - discipleshipStageRelevance: An object with numeric scores (0-1) for each of the following keys, representing the relevance of the verse to each discipleship stage:
      - notCurrentlyBeliever: Not currently a believer
      - newBeliever: New Believer
      - growingDisciple: Growing Disciple
      - discipleMaker: Disciple Maker
      - sageStage: Sage stage
    - christianExperienceRelevance: An object with numeric scores (0-1) for each of the following keys, representing the verse's direct relevance to the unique spiritual, emotional, and situational needs of each Christian faith experience. 
      When scoring, consider:
        - Does the verse invite, welcome, or explain foundational truths? (no experience, new to faith)
        - Does it encourage perseverance, growth, or restoration? (grew up in faith, returned)
        - Does it address doubt, crisis, or God's faithfulness? (crisis in faith)
        - Does it offer grace, forgiveness, or hope for new beginnings? (walked away, returning)
      Keys:
        - noExperienceYet: No Christian Faith experience yet (invitation, belonging)
        - newToFaith: New to the Christian Faith (foundational truths, welcome)
        - grewUpStillWalking: Grew up in the Faith and is still walking with God (perseverance, growth)
        - cameToFaithNowWalking: Came to Faith at some point and is now walking with God (growth, discipleship)
        - crisisLeaningIn: Currently in a crisis of Faith – leaning in (doubt, hope, comfort)
        - crisisLeaningOut: Currently in a crisis of Faith – leaning out (doubt, struggle, God's pursuit)
        - walkedAwayCurrently: Walked Away from the Faith Currently (grace, invitation, hope, anger, frustration)
        - walkedAwayNowOpen: Walked Away at one point, but is now Open Again (restoration, new beginnings)
        - walkedAwayNowReturned: Walked Away, but has now returned to the Faith (forgiveness, welcome, growth)

    Return only valid JSON, no extra commentary.
  `;

  console.log('2. OpenAI Prompt:', prompt);

  try {
    const response = await openai.chat.completions.create({
      model: "gpt-4",
      messages: [
        {
          role: "system",
          content: "You are a biblical analysis tool. Respond only with the requested JSON format containing numeric scores between 0 and 1."
        },
        {
          role: "user",
          content: prompt
        }
      ]
    });

    console.log('3. OpenAI Response:', response.choices[0].message.content);

    const analysis = JSON.parse(response.choices[0].message.content);
    console.log('4. Parsed Analysis:', analysis);

    if (analysis.themeMetadata) {
      const validatedScores = {
        themeMetadata: {
          emotional: parseFloat(analysis.themeMetadata.emotional),
          spiritual: parseFloat(analysis.themeMetadata.spiritual),
          relational: parseFloat(analysis.themeMetadata.relational),
          vocational: parseFloat(analysis.themeMetadata.vocational),
          physical: parseFloat(analysis.themeMetadata.physical),
          missional: parseFloat(analysis.themeMetadata.missional)
        }
      };

      console.log('5. Validated Scores:', validatedScores);
      return validatedScores;
    } else {
      console.error('❌ No themeMetadata in response');
      return null;
    }
  } catch (error) {
    console.error('❌ Error:', error);
    return null;
  }
};

// Update the main function to use this debug version
export const analyzeVerse = async (verse, selectedFields) => {
  try {
    console.log(`Starting analysis for verse ${verse.reference}...`);

    // Expanded prompt for all fields, preserving specific language
    const prompt = `
      Given the following Bible verse, generate a JSON object with the following fields. If a field is not directly inferable, use your best judgment or leave it as an empty string or empty array.

      Reference: ${verse.reference}
      Text: ${verse.text}

      Please provide the following fields:
      - bibleUrl: A direct Bible.com URL to the NIV version of this verse (format: https://www.bible.com/bible/111/{book}.{chapter}.{verse}.NIV)
      - reflectionQuestion: Write a single, practical reflection question for this verse that:
          - Encourages the reader to think about a specific situation or relationship in their life.
          - Is open-ended and invites personal storytelling, imagination, or self-examination.
          - Avoids simply restating the verse or asking generic "How do you apply X?" questions.
          - Uses varied question structures (e.g., "If you could...", "When was a time...", "Who in your life...", "What would it look like if...", "Why do you think...", "Describe a moment when...").
          - Is gentle and invitational, not confrontational or threatening.
          - Is appropriate for a wide range of personalities and spiritual maturity levels.
          - Avoids yes/no questions and encourages deeper thought or sharing.
          - Example: Instead of "How do you apply faith to a friendship?" ask "If there was one friendship situation where you could apply faith, which one would it be and why?"
          - Vary the style and focus of the question for each verse, so that not all questions follow the same template.
      - tags: 3-5 relevant, comma-separated keywords (themes or topics found in this verse)
      - categories: 2-3 broad categories (e.g., Spiritual Growth, Prayer, Doctrine, Relationships, Character, Mission/Evangelism)
      - growthArea: Exactly one of: Spiritual Development, Holisitic Personal Growth, Relational Development, Physical Development, Vocational Development, Missional Development, Emotional Development
      - devTopic: Exactly one of: Christian Character and Virtues, Cultural Engagement and Worldview, Dealing with Trials and Suffering, Discipleship and Mentoring Others, Evangelism and Sharing Your Faith, Finances - Handling Money Gods Way, Identity in Christ and Intrinsic Value, Leadership and Servanthood, Marriage and Family, Mental and Emotional Health, Moral and Ethical Decision-Making, Overcoming Temptation and Sin, Personal Faith and Relationship with God, Prayer and Spiritual Disciplines, Relationships and Community, Sexual Purity and Enjoyment, Spiritual Warfare, Stewardship of the Body, Time Management and Priorities, Understanding the Bible, Work and Stewardship
      - concept: Key theological or practical concept
      - difficultyLevel: Number 1-5 (1=foundational, 5=deep/complex)
      - relatedVerses: 2-4 comma-separated related verse references
      - contextualMeaning: 2-3 sentences explaining practical application, covering: the core message and principle, how it applies to modern life, and practical ways to implement this truth
      - themeMetadata: An object with numeric scores (0-1) for each of the following:
        - emotional: how much the verse is about the emotional aspects of a human experience
        - spiritual: how much the verse is about the spiritual aspects of a human experience
        - relational: family, friends, community, etc. and how much the verse is about those relationships
        - vocational: vocational and/or scholastic application
        - physical: how much the verse is about the physical aspects of a human experience
        - missional: how much the verse is about the focus on having a calling to help change the world through sharing the gospel
      - ageRelevance: An object with numeric scores (0-1) for each of the following keys, representing the developmental appropriateness and relevance of the verse to each age/life stage. 
        When scoring, consider:
          - Language complexity (is it simple enough for a child, or more suited for adults?)
          - Abstractness of ideas (concrete for children, more abstract for older groups)
          - Emotional and spiritual maturity required to understand and apply the verse
          - Life experience needed to relate to the verse
        Keys:
          - elementary: Elementary Age (simple, concrete, basic truths)
          - middleSchool: Middle School (accessible, some abstract ideas)
          - highSchool: High School (can handle more challenge/abstractness)
          - youngAdult: Young Adult (mature reasoning, life application)
          - middleAge: Middle Age (life experience, deeper reflection)
          - wisdomYears: Wisdom Years (deepest reflection, legacy, perspective)
      - relationshipRelevance: An object with numeric scores (0-1) for each of the following keys, representing the verse's direct relevance to the unique relational, emotional, and life circumstances of each status. 
        When scoring, consider:
          - Does the verse address issues of identity, belonging, or friendship? (youth, singles)
          - Does it speak to romantic love, commitment, or marriage? (dating, engaged, married)
          - Does it offer wisdom for parenting or family life? (married with kids, grandparents)
          - Does it provide comfort, hope, or encouragement for those experiencing loss or transition? (separated, divorced, widowed)
        Keys:
          - youth: Youth (identity, friendship, growth)
          - singleYoungAdult: Single Young Adult (calling, purpose, community)
          - singleAdult: Single Adult (purpose, contentment, relationships)
          - dating: Dating (love, purity, discernment)
          - engaged: Engaged (commitment, preparation)
          - earlyMarriage: Early Marriage (foundation, unity)
          - experiencedMarriage: Experienced Marriage (endurance, partnership)
          - marriedWithYoungKids: Married with young kids (parenting, patience)
          - marriedWithOlderKids: Married with older kids (guidance, legacy)
          - grandparents: Grandparents (wisdom, blessing, legacy)
          - separated: Separated (healing, hope)
          - divorced: Divorced (restoration, comfort)
          - widowed: Widowed (comfort, hope, legacy)
      - discipleshipStageRelevance: An object with numeric scores (0-1) for each of the following keys, representing the relevance of the verse to each discipleship stage:
        - notCurrentlyBeliever: Not currently a believer
        - newBeliever: New Believer
        - growingDisciple: Growing Disciple
        - discipleMaker: Disciple Maker
        - sageStage: Sage stage
      - christianExperienceRelevance: An object with numeric scores (0-1) for each of the following keys, representing the verse's direct relevance to the unique spiritual, emotional, and situational needs of each Christian faith experience. 
        When scoring, consider:
          - Does the verse invite, welcome, or explain foundational truths? (no experience, new to faith)
          - Does it encourage perseverance, growth, or restoration? (grew up in faith, returned)
          - Does it address doubt, crisis, or God's faithfulness? (crisis in faith)
          - Does it offer grace, forgiveness, or hope for new beginnings? (walked away, returning)
        Keys:
          - noExperienceYet: No Christian Faith experience yet (invitation, belonging)
          - newToFaith: New to the Christian Faith (foundational truths, welcome)
          - grewUpStillWalking: Grew up in the Faith and is still walking with God (perseverance, growth)
          - cameToFaithNowWalking: Came to Faith at some point and is now walking with God (growth, discipleship)
          - crisisLeaningIn: Currently in a crisis of Faith – leaning in (doubt, hope, comfort)
          - crisisLeaningOut: Currently in a crisis of Faith – leaning out (doubt, struggle, God's pursuit)
          - walkedAwayCurrently: Walked Away from the Faith Currently (grace, invitation, hope)
          - walkedAwayNowOpen: Walked Away at one point, but is now Open Again (restoration, new beginnings)
          - walkedAwayNowReturned: Walked Away, but has now returned to the Faith (forgiveness, welcome, growth)

      Return only valid JSON, no extra commentary.
    `;

    const response = await openai.chat.completions.create({
      model: "gpt-4o",
      messages: [
        {
          role: "system",
          content: "You are a biblical scholar and spiritual mentor. Provide deep, practical analysis in valid JSON format. Focus on real-world application and spiritual growth."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      temperature: 0.5
    });

    console.log('Raw OpenAI response:', response.choices[0].message.content);
    
    try {
      let content = response.choices[0].message.content.trim();
      // Remove code block markers if present
      if (content.startsWith('```')) {
        content = content.replace(/^```[a-zA-Z]*\n?/, '').replace(/```$/, '').trim();
      }
      const analysis = JSON.parse(content);
      console.log('Parsed analysis:', analysis);
      return analysis;
    } catch (parseError) {
      console.error('JSON Parse Error:', parseError);
      console.log('Failed to parse response:', response.choices[0].message.content);
      throw new Error(`Invalid JSON response: ${parseError.message}`);
    }
  } catch (error) {
    console.error(`Error analyzing verse ${verse.reference}:`, error);
    throw error;
  }
};

// Add new function to check if a verse needs enrichment
const needsEnrichment = (verse, selectedFields) => {
  console.log('Checking verse:', verse.reference, 'with fields:', selectedFields);
  
  // Check if any selected fields are empty or missing
  if (selectedFields.contextualMeaning && !verse.contextualMeaning) {
    console.log('Needs enrichment: missing contextualMeaning');
    return true;
  }
  if (selectedFields.difficultyLevel && !verse.difficultyLevel) {
    console.log('Needs enrichment: missing difficultyLevel');
    return true;
  }
  if (selectedFields.relatedVerses && (!verse.relatedVerses || verse.relatedVerses.length === 0)) {
    console.log('Needs enrichment: missing relatedVerses');
    return true;
  }
  if (selectedFields.tags && (!verse.tags || verse.tags.length === 0)) {
    console.log('Needs enrichment: missing tags');
    return true;
  }
  if (selectedFields.categories && (!verse.categories || verse.categories.length === 0)) {
    console.log('Needs enrichment: missing categories');
    return true;
  }
  
  // Check theme metadata
  if (selectedFields.themeMetadata) {
    console.log('Checking themeMetadata:', verse.themeMetadata);
    if (!verse.themeMetadata) return true;
    const themes = ['emotional', 'spiritual', 'relational', 'vocational', 'physical', 'missional'];
    for (const theme of themes) {
      if (verse.themeMetadata[theme] === undefined || verse.themeMetadata[theme] === 0) {
        console.log('Needs enrichment: missing or zero theme score for', theme);
        return true;
      }
    }
  }
  
  console.log('Verse does not need enrichment');
  return false;
};

export const enrichVerseData = async (updateProgress, selectedFields, enrichMode = 'new', versesOverride = null) => {
  const failedVerses = [];
  try {
    console.log('🚀 Starting enrichment process with mode:', enrichMode);
    console.log('Selected fields:', selectedFields);

    let allVerses = [];
    if (versesOverride && Array.isArray(versesOverride)) {
      allVerses = versesOverride;
    } else {
      const versesRef = collection(db, 'verses');
      const snapshot = await getDocs(versesRef);
      allVerses = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    }

    console.log(`📚 Total verses in database: ${allVerses.length}`);

    // Filter verses based on enrichment mode
    const versesToEnrich = enrichMode === 'new' 
      ? allVerses.filter(verse => needsEnrichment(verse, selectedFields))
      : allVerses;

    console.log('Verses needing enrichment:', versesToEnrich.map(v => ({
      reference: v.reference,
      id: v.id,
      hasContextualMeaning: !!v.contextualMeaning,
      hasThemeMetadata: !!v.themeMetadata,
      hasTags: v.tags?.length > 0,
      hasCategories: v.categories?.length > 0
    })));

    if (versesToEnrich.length === 0) {
      console.log('No verses need enrichment!');
      return {
        updated: 0,
        errors: 0,
        failedVerses: [],
        finalCost: 0
      };
    }

    // Only show cost estimate and confirmation for bulk enrichment
    if (!versesOverride) {
      const estimate = await calculateCostEstimate(versesToEnrich);
      console.log(`💰 Estimated cost: $${estimate.cost}`);
      if (!window.confirm(
        `Mode: Enriching ${enrichMode === 'new' ? 'newly uploaded/empty' : 'all'} verses\n` +
        `Verses to process: ${versesToEnrich.length} out of ${allVerses.length} total verses\n` +
        `Estimated cost: $${estimate.cost} USD\n` +
        `Continue with enrichment?`
      )) {
        throw new Error('Operation cancelled by user');
      }
    }

    let updateCount = 0;

    for (const verse of versesToEnrich) {
      if (isCancelled) {
        failedVerses.push({
          reference: verse.reference,
          reason: 'Process cancelled by user'
        });
        break;
      }

      try {
        console.log(`Processing verse ${verse.reference} (${updateCount + 1}/${versesToEnrich.length})`);
        const analysis = await analyzeVerse(verse, selectedFields);

        if (analysis) {
          // Normalize tags and categories to arrays
          let tags = analysis.tags;
          if (typeof tags === 'string') tags = tags.split(',').map(t => t.trim()).filter(Boolean);
          if (!Array.isArray(tags)) tags = [];
          let categories = analysis.categories;
          if (typeof categories === 'string') categories = categories.split(',').map(c => c.trim()).filter(Boolean);
          if (!Array.isArray(categories)) categories = [];

          // If the verse has an id, update the doc
          if (verse.id) {
            await updateDoc(doc(db, 'verses', verse.id), {
              ...analysis,
              tags,
              categories,
              lastUpdated: new Date()
            });
          }

          updateCount++;
          if (typeof updateProgress === 'function') {
            const progress = Math.round((updateCount / versesToEnrich.length) * 100);
            updateProgress(progress, verse.reference);
          }
          // Yield to event loop to allow UI update
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      } catch (error) {
        console.error(`Failed to update verse ${verse.reference}:`, error);
        failedVerses.push({
          reference: verse.reference,
          reason: error.message,
          error: error.toString()
        });
      }
    }

    const summary = {
      updated: updateCount,
      errors: failedVerses.length,
      failedVerses,
      finalCost: 0
    };

    // Log detailed summary
    console.log('Enrichment Summary:', {
      totalProcessed: versesToEnrich.length,
      successfulUpdates: updateCount,
      failedUpdates: failedVerses.length,
      failedVerses: failedVerses.map(f => ({
        verse: f.reference,
        reason: f.reason
      }))
    });

    return summary;
  } catch (error) {
    console.error('Enrichment process error:', error);
    failedVerses.push({
      reference: 'Process Error',
      reason: error.message,
      error: error.toString()
    });
    throw error;
  }
};

export const runVerseEnrichment = async (updateProgress, selectedFields, enrichMode) => {
  try {
    console.log('Starting verse enrichment...');
    const result = await enrichVerseData(updateProgress, selectedFields, enrichMode);
    const message = `Enrichment complete!\n` +
      `Successfully enriched: ${result.updated} verses\n` +
      `Failed: ${result.errors} verses\n` +
      `Cost: $${result.finalCost}\n\n` +
      (result.failedVerses.length > 0 ? 
        `Failed verses:\n${result.failedVerses.map(f => 
          `- ${f.reference}: ${f.reason}`
        ).join('\n')}` : '');
    
    alert(message);
    return result;
  } catch (error) {
    console.error('Error during verse enrichment:', error);
    throw error;
  }
};

